import { forwardRef } from "react";
import { format } from "date-fns";

const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/ym9v5wop_7B92B142-D2AF-47C9-93FB-F3C3C1AB10EF.PNG";

const SERVICE_TYPE_LABELS = {
  tyres_only: "Tyres Only",
  tyres_alignment: "Tyres + Wheel Alignment",
  tyres_rotation: "Tyres + Rotation",
  tyres_alignment_rotation: "Tyres + Alignment + Rotation",
  alignment_only: "Wheel Alignment Only",
  alignment_rotation: "Alignment + Rotation",
  rotation_only: "Tyre Rotation Only"
};

const getServicesLabel = (service) => {
  if (service?.selected_services && service.selected_services.length > 0) {
    const parts = [];
    if (service.selected_services.includes("tyres")) parts.push("Tyres");
    if (service.selected_services.includes("alignment")) parts.push("Wheel Alignment");
    if (service.selected_services.includes("rotation")) parts.push("Tyre Rotation");
    if (parts.length > 0) return parts.join(" + ");
  }
  if (service?.type && SERVICE_TYPE_LABELS[service.type]) {
    return SERVICE_TYPE_LABELS[service.type];
  }
  return service?.type || "Tyre Service";
};

const PAYMENT_METHOD_LABELS = {
  cash: "Cash",
  eftpos: "EFTPOS",
  credit_card: "Credit Card",
  bank_transfer: "Bank Transfer",
  other: "Other"
};

const Receipt = forwardRef(({ data, variant = "customer" }, ref) => {
  if (!data) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return format(new Date(dateStr), "dd MMMM yyyy");
    } catch {
      return dateStr;
    }
  };

  const formatAddress = (address) => {
    if (!address) return "N/A";
    if (typeof address === "string") return address;
    return address.full_address || [
      address.street_number,
      address.street_name,
      address.suburb,
      address.city,
      address.postcode
    ].filter(Boolean).join(", ") || "N/A";
  };

  const completionDetails = data.completion || {};
  const paymentInfo = data.payment || {};

  return (
    <div 
      ref={ref}
      className="bg-white text-gray-900 p-8 max-w-[800px] mx-auto"
      style={{ fontFamily: "Arial, sans-serif" }}
    >
      <style>
        {`
          @media print {
            .print-no-break {
              break-inside: avoid !important;
              page-break-inside: avoid !important;
            }
            .pricing-section {
              break-inside: avoid !important;
              page-break-inside: avoid !important;
            }
          }
        `}
      </style>
      
      {/* Header */}
      <div className="flex justify-between items-start border-b-2 border-gray-200 pb-6 mb-6 print-no-break">
        <div>
          <img src={LOGO_LIGHT} alt="TyreMatch" className="h-20 mb-2" style={{ maxWidth: "240px" }} />
          <p className="text-sm text-gray-600">www.tyrematch.co.nz</p>
          <p className="text-sm text-gray-600">support@tyrematch.co.nz</p>
        </div>
        <div className="text-right">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">SERVICE COMPLETION</h1>
          <h2 className="text-lg font-semibold text-gray-700 mb-2">CONFIRMATION</h2>
          <p className="text-sm text-gray-600">
            Job ID: <span className="font-semibold text-orange-600">#{data.order_id?.slice(0, 8).toUpperCase()}</span>
          </p>
          <p className="text-sm text-gray-600 mt-1">
            Completion Date: {formatDate(completionDetails.service_date || data.paid_at)}
          </p>
        </div>
      </div>

      {/* Customer & Service Provider */}
      <div className="grid grid-cols-2 gap-8 mb-8 print-no-break">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Customer</h3>
          <p className="font-semibold text-lg">{data.customer?.name || "Customer"}</p>
          <p className="text-gray-600 text-sm mt-1">{data.customer?.email}</p>
          {data.service?.vehicle_rego && (
            <p className="text-gray-600 text-sm mt-2">Vehicle Rego: <span className="font-medium">{data.service.vehicle_rego}</span></p>
          )}
        </div>

        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Service Provider</h3>
          <p className="font-semibold text-lg">{data.shop?.name || "Tyre Shop"}</p>
          <p className="text-gray-600 text-sm mt-2">{formatAddress(data.shop?.address)}</p>
        </div>
      </div>

      {/* Services Completed */}
      <div className="mb-8 print-no-break">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Services Completed</h3>
        <div className="bg-orange-50 border border-orange-100 rounded-lg p-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Services Provided</p>
              <p className="font-medium">{getServicesLabel(data.service)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Vehicle Registration</p>
              <p className="font-medium">{data.service?.vehicle_rego || "N/A"}</p>
            </div>
            {(data.service?.selected_services?.includes("tyres") || data.service?.type?.includes("tyres")) && (
              <>
                <div>
                  <p className="text-sm text-gray-500">Tyre Brand & Model</p>
                  <p className="font-medium">{data.service?.tyre_brand} {data.service?.tyre_model}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Tyre Size</p>
                  <p className="font-medium">{data.service?.tyre_size || "N/A"}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Quantity</p>
                  <p className="font-medium">{data.service?.quantity || 4} tyres</p>
                </div>
              </>
            )}
            {data.service?.notes && (
              <div className="col-span-2">
                <p className="text-sm text-gray-500">Notes</p>
                <p className="font-medium">{data.service.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Service Date & Time */}
      {(completionDetails.service_date || data.appointment?.proposed_date) && (
        <div className="mb-8 print-no-break">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Service Date</h3>
          <div className="bg-green-50 border border-green-100 rounded-lg p-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="font-semibold text-lg">
                  {completionDetails.service_date 
                    ? formatDate(completionDetails.service_date) 
                    : formatDate(data.appointment?.proposed_date)}
                </p>
                <p className="text-gray-600">
                  {completionDetails.service_time || data.appointment?.proposed_time || ""}
                </p>
                {(completionDetails.completion_notes || data.appointment?.shop_notes) && (
                  <p className="text-sm text-gray-500 mt-1">
                    {completionDetails.completion_notes || data.appointment.shop_notes}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Summary */}
      <div className="pricing-section">
        <div className="mb-8">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Service Pricing</h3>
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-left py-3 text-gray-600 font-semibold">Description</th>
                <th className="text-right py-3 text-gray-600 font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.pricing?.breakdown && Object.entries(data.pricing.breakdown).map(([key, value]) => {
                if (typeof value !== 'number' || value <= 0) return null;
                const keyLabels = {
                  tyre_fitment: "Tyres & Fitment (incl. balancing & stewardship)",
                  tyres: "Tyres & Fitment",
                  alignment: "Wheel Alignment",
                  rotation: "Tyre Rotation"
                };
                const displayName = keyLabels[key] || key.replace(/_/g, " ");
                return (
                  <tr key={key} className="border-b border-gray-100">
                    <td className="py-3 capitalize">{displayName}</td>
                    <td className="py-3 text-right">${value.toFixed(2)}</td>
                  </tr>
                );
              })}
              {(!data.pricing?.breakdown || 
                !Object.entries(data.pricing.breakdown).some(([, v]) => typeof v === 'number' && v > 0)) && (
                <tr className="border-b border-gray-100">
                  <td className="py-3">Service Total</td>
                  <td className="py-3 text-right">${data.pricing?.total_incl_gst?.toFixed(2)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Total */}
        <div className="bg-gray-900 text-white rounded-lg p-6 mb-8 print-no-break">
          <div className="flex justify-between items-center">
            <span className="text-xl font-bold">Total Paid</span>
            <span className="text-2xl font-bold text-orange-400">
              ${data.pricing?.total_incl_gst?.toFixed(2)} {data.pricing?.currency}
            </span>
          </div>
          <p className="text-gray-400 text-sm mt-2">
            Paid at shop via {paymentInfo.method 
              ? (PAYMENT_METHOD_LABELS[paymentInfo.method] || paymentInfo.method) 
              : "Direct Payment"}
          </p>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 print-no-break">
        <p className="text-xs text-gray-500 leading-relaxed">
          Payment for this service was made directly to the service provider. TyreMatch is a marketplace platform and is not the seller or payment processor for this transaction.
        </p>
      </div>

      {/* Footer */}
      <div className="text-center text-sm text-gray-500 border-t border-gray-200 pt-6 print-no-break">
        <p className="font-semibold mb-2">Thank you for choosing TyreMatch!</p>
        <p className="text-xs text-gray-400 mb-2">New Zealand's smartest way to compare tyre quotes and book services.</p>
        <p>For queries, contact support@tyrematch.co.nz</p>
        <p className="mt-2">Job ID: {data.order_id}</p>
      </div>
    </div>
  );
});

Receipt.displayName = "Receipt";

export default Receipt;
